//----------------------------------------------------------------------------------------------------------------------
// Binding Manager
//----------------------------------------------------------------------------------------------------------------------

import { GameEventBus } from '../classes/eventBus.ts';

// Interfaces
import {
    type Binding,
    type BindingDefinition,
    type CaptureInputOptions,
    type Context,
    type InputDefinition,
    bindingTypes,
} from '../interfaces/binding.ts';
import type { Disposable } from '../interfaces/lifecycle.ts';
import {
    type DeviceType,
    type DeviceValueReader,
    type DeviceValueReaderDefinition,
    type InputDevice,
    type InputState,
    isGamepadState,
    isKeyboardState,
    isMouseState,
} from '../interfaces/input.ts';
import type { LoggerInterface } from '../interfaces/logger.ts';
import type { Action } from '../interfaces/action.ts';

// Bindings
import { TriggerBinding } from '../classes/bindings/trigger.ts';
import { ToggleBinding } from '../classes/bindings/toggle.ts';
import { ValueBinding } from '../classes/bindings/value.ts';

// Value Readers
import { KeyboardValueReader } from '../classes/input/readers/keyboard.ts';
import { MouseValueReader } from '../classes/input/readers/mouse.ts';
import { GamepadValueReader } from '../classes/input/readers/gamepad.ts';

// Utils
import { LoggingUtility, SAGELogger } from '../utils/logger.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Configuration object structure for binding manager
 */
export interface BindingConfiguration
{
    /** All registered actions */
    actions : Action[];

    /** All registered bindings */
    bindings : BindingDefinition[];

    /** All registered contexts */
    contexts : {
        name : string;
        exclusive : boolean;
        active : boolean;
    }[];
}

/**
 * Minimum axis magnitude for a gamepad event to count as intentional activity.
 * This is separate from per-binding deadzones — it just prevents idle stick drift
 * from flipping the last-active device type.
 */
const GAMEPAD_ACTIVITY_DEADZONE = 0.05;

/**
 * Minimum axis magnitude for a gamepad event to count as intentional during captureInput().
 * Much higher than the activity deadzone to avoid false positives from stick drift.
 */
const CAPTURE_AXIS_DEADZONE = 0.5;

/**
 * Internal state tracked while a captureInput() call is in progress.
 */
interface CaptureState
{
    resolve : (result : InputDefinition) => void;
    reject : (reason : unknown) => void;
    options : CaptureInputOptions;
    snapshot : string[];
    abortCleanup ?: () => void;
}

/* eslint-disable no-continue */

/**
 * Manages input bindings and actions for the game
 */
export class BindingManager implements Disposable
{
    /** Map of device IDs to their bindings */
    private _bindings = new Map<string, Binding[]>();

    /** Map of action names to their action definitions */
    private _actions = new Map<string, Action>();

    /** Map of all registered contexts by name for O(1) lookup */
    private _contexts = new Map<string, Context>();

    /**
     * Set of all active contexts (both exclusive and non-exclusive)
     */
    private _activeContexts = new Set<string>();

    /** The most recent device type that produced intentional input */
    private _lastActiveDeviceType : DeviceType | null = null;

    /** Last input state per device, used to prime edge state on context activation */
    private _lastInputState = new Map<string, InputState>();

    /** Active captureInput() state, or null when not capturing */
    private _captureState : CaptureState | null = null;

    /** Event bus for handling game events */
    private _eventBus : GameEventBus;

    /** Unsubscribe function for input events */
    private _inputUnsubscribe : (() => void) | null = null;

    /** Logger instance */
    private _log : LoggerInterface;

    /**
     * Creates an instance of BindingManager.
     * Automatically subscribes to 'input:changed' events on the event bus.
     *
     * @param eventBus
     * @param logger
     */
    constructor(eventBus : GameEventBus, logger ?: LoggingUtility)
    {
        this._eventBus = eventBus;

        // Bind to input events
        this._inputUnsubscribe = this._eventBus.subscribe(
            'input:changed',
            (event) =>
            {
                const payload = event.payload as { device : InputDevice; state : InputState };
                if(payload)
                {
                    this.$handleInput(payload.device, payload.state);
                }
            }
        );

        this._log = logger?.getLogger('BindingManager') || new SAGELogger('BindingManager');
        this._log.debug('BindingManager initialized');
    }

    //------------------------------------------------------------------------------------------------------------------
    // Private Methods
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Checks if a binding's context is currently active.
     * Bindings with no context are always considered active.
     *
     * @param binding
     */
    private _isBindingContextActive(binding : Binding) : boolean
    {
        // If no context is specified, binding is always active regardless of active contexts
        if(!binding.context)
        {
            return true;
        }

        // Otherwise check if the binding's specific context is active
        return this._activeContexts.has(binding.context);
    }

    /**
     * Get a context by name, creating it if it doesn't exist.
     *
     * @param contextName
     * @param exclusive - Only used when creating a new context
     */
    private _getOrCreateContext(contextName : string, exclusive = true) : Context
    {
        let context = this._contexts.get(contextName);

        if(!context)
        {
            context = {
                name: contextName,
                exclusive,
            };

            this._contexts.set(contextName, context);
            this._log.debug(`Auto-created context "${ contextName }" (exclusive: ${ exclusive })`);
        }

        return context;
    }

    /**
     * Deactivate all exclusive contexts except the specified one.
     * Non-exclusive contexts are left untouched.
     *
     * @param exceptContextName
     */
    private _deactivateExclusiveContexts(exceptContextName ?: string) : string[]
    {
        const deactivatedContexts : string[] = [];

        // Find all active exclusive contexts
        for(const contextName of this._activeContexts)
        {
            // Skip the context we want to keep
            if(contextName === exceptContextName)
            {
                continue;
            }

            const context = this._contexts.get(contextName);

            // If this is an exclusive context, deactivate it
            if(context?.exclusive)
            {
                this._activeContexts.delete(contextName);
                deactivatedContexts.push(contextName);
            }
        }

        return deactivatedContexts;
    }

    /**
     * Determines whether an input event represents intentional activity that should
     * update the last-active device type. Filters out noise like idle mousemove.
     *
     * @param state
     */
    private _shouldUpdateActiveDevice(state : InputState) : boolean
    {
        if(isMouseState(state))
        {
            // Only clicks and wheel count — mousemove alone is noise
            return Object.values(state.buttons).some((btn) => btn.pressed) || state.wheel !== undefined;
        }

        if(isGamepadState(state))
        {
            // Any button pressed or any axis past the activity deadzone
            return Object.values(state.buttons).some((btn) => btn.pressed)
                || Object.values(state.axes).some((val) => Math.abs(val) > GAMEPAD_ACTIVITY_DEADZONE);
        }

        // Keyboard events are always intentional
        return true;
    }

    /**
     * Create a binding from a binding definition.
     *
     * @param definition
     * @returns Null if the action is not registered or the binding type is unsupported
     */
    private _createBindingFromDefinition(definition : BindingDefinition) : Binding | null
    {
        // Get the full action object from the action name
        const action = this._actions.get(definition.action);

        // If the action doesn't exist, log a warning and return null
        if(!action)
        {
            this._log.warn(`Cannot create binding: Action "${ definition.action }" not found.`);
            return null;
        }

        // Extract deviceID and create reader from the input definition
        const { deviceID, ...readerDef } = definition.input;

        switch (definition.type)
        {
            case 'trigger': {
                // Create the binding with the device value reader based on definition's source
                return new TriggerBinding(
                    action,
                    deviceID,
                    definition.input.type,
                    this._createInputSourceFromDefinition(readerDef),
                    {
                        ...(definition.options || {}),
                        context: definition.context,
                    }
                );
            }

            case 'toggle': {
                // Create the binding with the device value reader based on definition's source
                return new ToggleBinding(
                    action,
                    deviceID,
                    definition.input.type,
                    this._createInputSourceFromDefinition(readerDef),
                    {
                        ...(definition.options || {}),
                        context: definition.context,
                    }
                );
            }

            case 'value': {
                // Create the binding with the device value reader based on definition's source
                return new ValueBinding(
                    action,
                    deviceID,
                    definition.input.type,
                    this._createInputSourceFromDefinition(readerDef),
                    {
                        ...(definition.options || {}),
                        context: definition.context,
                    }
                );
            }

            default:
                this._log.error(`Binding type not implemented: ${ (definition as BindingDefinition).type }`);
                return null;
        }
    }

    /**
     * Create a device value reader from a definition object.
     *
     * @param definition
     * @throws Error if the reader type is not supported
     */
    private _createInputSourceFromDefinition(definition : DeviceValueReaderDefinition) : DeviceValueReader
    {
        // Create reader based on device type
        switch (definition.type)
        {
            case 'keyboard':
                return new KeyboardValueReader(
                    definition.sourceKey,
                    definition.options
                );

            case 'mouse':
            {
                // Check if sourceType is valid
                const sourceType = definition.sourceType;
                if(!(sourceType === 'button' || sourceType === 'position' || sourceType === 'wheel'))
                {
                    throw new Error(`Invalid mouse source type: ${ sourceType }`);
                }

                return new MouseValueReader(
                    sourceType,
                    definition.sourceKey
                );
            }

            case 'gamepad':
            {
                // Check if sourceType is valid
                const sourceType = definition.sourceType;
                if(!(sourceType === 'button' || sourceType === 'axis'))
                {
                    throw new Error(`Invalid gamepad source type: ${ sourceType }`);
                }
                return new GamepadValueReader(
                    sourceType,
                    definition.sourceKey,
                    definition.options
                );
            }

            default:
                throw new Error(`Unsupported input source type: ${ (definition as DeviceValueReaderDefinition).type }`);
        }
    }

    /**
     * Evaluates an input event during capture mode. If the event matches the capture criteria,
     * resolves the capture promise and cleans up. Otherwise does nothing and waits for the next event.
     *
     * @param device
     * @param state
     */
    private _processCaptureInput(device : InputDevice, state : InputState) : void
    {
        const capture = this._captureState;
        if(!capture)
        {
            return;
        }

        const { options } = capture;

        // Filter by device type
        if(!options.deviceTypes.includes(device.type))
        {
            return;
        }

        let result : InputDefinition | null = null;

        if(isKeyboardState(state))
        {
            // Only capture key-down, ignore key repeats
            if(state.event?.repeat === true)
            {
                return;
            }

            // Find the first key that transitioned to pressed
            for(const key of Object.keys(state.delta))
            {
                if(state.delta[key] === true)
                {
                    result = { type: 'keyboard', sourceType: 'key', sourceKey: key, deviceID: device.id };
                    break;
                }
            }
        }
        else if(isMouseState(state))
        {
            // Only capture button presses, ignore position and wheel
            for(const key of Object.keys(state.buttons))
            {
                if(state.buttons[key].pressed)
                {
                    result = { type: 'mouse', sourceType: 'button', sourceKey: key, deviceID: device.id };
                    break;
                }
            }
        }
        else if(isGamepadState(state))
        {
            // Check buttons first
            for(const key of Object.keys(state.buttons))
            {
                if(state.buttons[key].pressed)
                {
                    result = { type: 'gamepad', sourceType: 'button', sourceKey: key, deviceID: device.id };
                    break;
                }
            }

            // Then check axes if no button found
            if(!result)
            {
                for(const key of Object.keys(state.axes))
                {
                    if(Math.abs(state.axes[key]) > CAPTURE_AXIS_DEADZONE)
                    {
                        result = { type: 'gamepad', sourceType: 'axis', sourceKey: key, deviceID: device.id };
                        break;
                    }
                }
            }
        }

        // No intentional input detected
        if(!result)
        {
            return;
        }

        // Apply sourceTypes filter — if it doesn't match, keep waiting
        if(options.sourceTypes
            && !options.sourceTypes.includes(result.sourceType as typeof options.sourceTypes[number])
        )
        {
            return;
        }

        // We have a match — resolve and clean up
        const { resolve } = capture;
        this._cleanupCapture();
        resolve(result);
    }

    /**
     * Tears down capture state, deactivates the temporary context, and restores the
     * snapshotted contexts. Does not resolve or reject — the caller handles that.
     */
    private _cleanupCapture() : void
    {
        const capture = this._captureState;
        if(!capture)
        {
            return;
        }

        // Clean up abort listener
        if(capture.abortCleanup)
        {
            capture.abortCleanup();
        }

        const { snapshot } = capture;
        this._captureState = null;

        // Remove the temporary context entirely and restore previous state
        this.deactivateContext('__sage_capture__');
        this._contexts.delete('__sage_capture__');

        for(const contextName of snapshot)
        {
            this.activateContext(contextName);
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // Internal API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Handle input from a device and process all relevant bindings.
     *
     * **Internal API**: This method is prefixed with `$` to indicate it is intended for internal
     * engine use only. While it is public for testing purposes, external code should not call
     * this method directly. Input handling is triggered automatically via the event bus.
     *
     * @param device - The input device
     * @param state - Current input state
     *
     * @internal
     */
    public $handleInput(device : InputDevice, state : InputState) : void
    {
        // Snapshot input state so context activations can prime edge state from physical reality
        this._lastInputState.set(device.id, state);

        // Track last-active device type before processing bindings.
        // This runs even if there are no bindings for this device.
        if(this._shouldUpdateActiveDevice(state) && device.type !== this._lastActiveDeviceType)
        {
            const previousDeviceType = this._lastActiveDeviceType;
            this._lastActiveDeviceType = device.type;
            this._eventBus.publish({
                type: 'input:device-type:changed',
                payload: { deviceType: device.type, previousDeviceType },
            });
        }

        // If capturing, route to the capture handler and suppress all normal binding processing
        if(this._captureState)
        {
            this._processCaptureInput(device, state);
            return;
        }

        const bindings = this._bindings.get(device.id);
        if(!bindings || bindings.length === 0)
        {
            return;
        }

        if(this._activeContexts.size === 0 && bindings.some((binding) => binding.context))
        {
            return;
        }

        for(const binding of bindings)
        {
            if(!this._isBindingContextActive(binding))
            {
                continue;
            }

            binding.process(state, this._eventBus);
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // Action Management API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Registers an action.
     *
     * @param action
     * @throws Error if action is already registered
     */
    public registerAction(action : Action) : void
    {
        this._log.debug(`Registering action "${ action.name }"`);

        if(this._actions.has(action.name))
        {
            const errorMsg = `Action "${ action.name }" already registered.`;
            this._log.error(errorMsg);
            throw new Error(errorMsg);
        }

        this._actions.set(action.name, action);
        this._log.debug(`Action "${ action.name }" registered successfully`);
    }

    /**
     * Gets an action by name.
     *
     * @param actionName
     */
    public getAction(actionName : string) : Action | null
    {
        this._log.trace(`Getting action "${ actionName }"`);

        const action = this._actions.get(actionName) ?? null;
        if(!action)
        {
            this._log.debug(`Action "${ actionName }" not found`);
        }

        return action;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Device Tracking API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Returns the device type that most recently produced intentional input,
     * or `null` if no input has been received yet.
     */
    public get lastActiveDeviceType() : DeviceType | null
    {
        return this._lastActiveDeviceType;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Input Capture API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Waits for the next intentional input event and returns a descriptor suitable for constructing
     * a binding. While capturing, all normal bindings are suppressed via a temporary exclusive context.
     * Previous context state is restored when the capture completes or is cancelled.
     *
     * @param options
     * @throws Error if a capture is already in progress
     */
    public captureInput(options : CaptureInputOptions) : Promise<InputDefinition>
    {
        if(this._captureState)
        {
            throw new Error('captureInput() already in progress');
        }

        // Snapshot current active contexts before suppression
        const snapshot = [ ...this._activeContexts ];

        // Push a temporary exclusive context to suppress all normal bindings
        this.registerContext('__sage_capture__', true);
        this.activateContext('__sage_capture__');

        return new Promise<InputDefinition>((resolve, reject) =>
        {
            const captureState : CaptureState = { resolve, reject, options, snapshot };
            this._captureState = captureState;

            // Wire up abort signal if provided
            if(options.signal)
            {
                if(options.signal.aborted)
                {
                    this._cleanupCapture();
                    reject(options.signal.reason ?? new Error('captureInput() aborted'));
                    return;
                }

                const onAbort = () : void =>
                {
                    const reason = options.signal?.reason ?? new Error('captureInput() aborted');
                    this._cleanupCapture();
                    reject(reason);
                };

                options.signal.addEventListener('abort', onAbort, { once: true });

                captureState.abortCleanup = () =>
                {
                    options.signal?.removeEventListener('abort', onAbort);
                };
            }
        });
    }

    //------------------------------------------------------------------------------------------------------------------
    // Context Management API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Registers a context with specific options.
     * Updates exclusivity if the context already exists.
     *
     * @param contextName
     * @param exclusive
     */
    public registerContext(contextName : string, exclusive = true) : Context
    {
        const context = this._getOrCreateContext(contextName, exclusive);

        // Update context's exclusivity if it already exists
        if(context.exclusive !== exclusive)
        {
            context.exclusive = exclusive;
            this._log.info(`Updated context "${ contextName }" exclusivity: ${ exclusive }`);
        }

        return context;
    }

    /**
     * Activates a context, enabling all bindings associated with it.
     * If the context is exclusive, all other exclusive contexts will be deactivated.
     *
     * @param contextName
     */
    public activateContext(contextName : string) : void
    {
        // Get or create context
        const context = this._getOrCreateContext(contextName);

        // Get the context's exclusivity setting
        const isExclusive = context.exclusive;

        this._log.debug(`Activating context "${ contextName }" (exclusive: ${ isExclusive })`);

        // If already active, nothing to do unless we're making an exclusive activation
        const isAlreadyActive = this._activeContexts.has(contextName);

        if(isExclusive)
        {
            // Deactivate all other exclusive contexts
            const deactivated = this._deactivateExclusiveContexts(contextName);

            if(deactivated.length > 0)
            {
                this._log.info(`Deactivated exclusive contexts: ${ deactivated.join(', ') }`);
            }
        }

        // Add to active contexts if not already there
        if(!isAlreadyActive)
        {
            this._activeContexts.add(contextName);

            // Prime edge state on all bindings in this context from the last known physical input.
            // This prevents already-held inputs from producing phantom edges in the new context while
            // still allowing genuinely new inputs to fire.
            for(const [ deviceId, deviceBindings ] of this._bindings.entries())
            {
                const lastState = this._lastInputState.get(deviceId);
                for(const binding of deviceBindings)
                {
                    if(binding.context === contextName)
                    {
                        binding.resetEdgeState(lastState);
                    }
                }
            }

            this._log.info(`Context "${ contextName }" activated${ isExclusive ? ' as exclusive' : '' }`);
        }
    }

    /**
     * Deactivates a context, disabling all bindings associated with it.
     *
     * @param contextName
     */
    public deactivateContext(contextName : string) : void
    {
        this._log.debug(`Deactivating context "${ contextName }"`);

        if(this._activeContexts.has(contextName))
        {
            this._activeContexts.delete(contextName);
            this._log.info(`Context "${ contextName }" deactivated`);
        }
        else
        {
            this._log.debug(`Context "${ contextName }" was not active`);
        }
    }

    /**
     * Returns a list of all active contexts.
     */
    public getActiveContexts() : string[]
    {
        return [ ...this._activeContexts ];
    }

    /**
     * Returns whether a context is active.
     *
     * @param contextName
     */
    public isContextActive(contextName : string) : boolean
    {
        return this._activeContexts.has(contextName);
    }

    /**
     * Gets a context by name.
     *
     * @param contextName
     */
    public getContext(contextName : string) : Context | null
    {
        return this._contexts.get(contextName) || null;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Binding Management API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Register a binding for an input device.
     * Auto-registers the binding's context if not already known.
     *
     * @param binding
     * @throws Error if binding type is invalid
     */
    public $registerBinding(binding : Binding) : void
    {
        if(!bindingTypes.includes(binding.type))
        {
            throw new Error(`Invalid binding type: ${ binding.type }`);
        }

        // If binding has a context but context isn't registered yet, register it with default settings
        if(binding.context && !this._contexts.has(binding.context))
        {
            this.registerContext(binding.context);
        }

        // Initialize device bindings array if it doesn't exist
        if(!this._bindings.has(binding.deviceID))
        {
            this._bindings.set(binding.deviceID, []);
        }

        // Add the binding
        this._bindings.get(binding.deviceID)?.push(binding);
        this._log.debug(`Registered ${ binding.type } binding for "${ binding.action.name }" in context `
            + `"${ binding.context || null }"`);
    }

    /**
     * Register a binding using a binding definition object.
     * Creates the appropriate Binding subclass and registers it.
     *
     * @param definition
     */
    public registerBinding(definition : BindingDefinition) : void
    {
        const binding = this._createBindingFromDefinition(definition);

        if(binding)
        {
            this.$registerBinding(binding);
        }
        else
        {
            this._log.error(`Failed to create binding for action "${ definition.action }" with type `
                + `"${ definition.type }"`);
        }
    }

    /**
     * Unregister all bindings for an action within a context.
     *
     * @param actionName
     * @param context - Pass null (default) to target context-free bindings
     */
    public unregisterBindings(actionName : string, context : string | null = null) : void
    {
        this._log.debug(`Unregistering all bindings for action "${ actionName }" in context "${ context }"`);

        let bindingsRemoved = 0;

        // Iterate through all devices and their bindings
        for(const [ deviceId, deviceBindings ] of this._bindings.entries())
        {
            const newBindings = deviceBindings.filter((binding) =>
            {
                const bindingContext = binding.context || null;
                const shouldKeep = binding.action.name !== actionName || bindingContext !== context;
                if(!shouldKeep)
                {
                    bindingsRemoved++;
                }
                return shouldKeep;
            });

            // Only update the map if we actually removed something
            if(newBindings.length !== deviceBindings.length)
            {
                this._bindings.set(deviceId, newBindings);
            }
        }

        this._log.info(`Removed ${ bindingsRemoved } bindings for action "${ actionName }" in context "${ context }"`);
    }

    /**
     * Gets all bindings for a specific action, optionally filtered by device type and/or context.
     *
     * @param actionName
     * @param deviceType - If provided, filters to only bindings for that device type
     * @param context - If provided, filters to only bindings in that context
     */
    public getBindingsForAction(actionName : string, deviceType ?: DeviceType, context ?: string | null) : Binding[]
    {
        const result : Binding[] = [];

        for(const deviceBindings of this._bindings.values())
        {
            for(const binding of deviceBindings)
            {
                if(binding.action.name !== actionName) { continue; }
                if(deviceType !== undefined && binding.deviceType !== deviceType) { continue; }

                const bindingContext = binding.context || null;
                if(context !== undefined && bindingContext !== context) { continue; }

                result.push(binding);
            }
        }

        return result;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Configuration Management API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Exports the current configuration to a serializable object.
     * Includes all actions, bindings, contexts, and their active states.
     */
    public exportConfiguration() : BindingConfiguration
    {
        this._log.debug('Exporting binding configuration');

        // Export all registered actions
        const actions = [ ...this._actions.values() ].map((action) =>
        {
            // Ensure we include all analog action properties when exporting
            if(action.type === 'analog')
            {
                return {
                    name: action.name,
                    type: action.type,
                    label: action.label,
                    minValue: action.minValue ?? 0,
                    maxValue: action.maxValue ?? 1,
                };
            }
            return action;
        });

        // Export all registered bindings using their toJSON methods
        const bindings : BindingDefinition[] = [];

        // Iterate through all device bindings
        for(const deviceBindings of this._bindings.values())
        {
            for(const binding of deviceBindings)
            {
                // Let the binding serialize itself to the BindingDefinition format
                bindings.push(binding.toJSON());
            }
        }

        // Export all contexts with their active state
        const contexts = [ ...this._contexts.values() ].map((context) => ({
            name: context.name,
            exclusive: context.exclusive,
            active: this._activeContexts.has(context.name),
        }));

        this._log.info(`Configuration exported: ${ actions.length } actions, ${ bindings.length } bindings, `
            + `${ contexts.length } contexts`);

        return {
            actions,
            bindings,
            contexts,
        };
    }

    /**
     * Imports a configuration, replacing all current actions, bindings, and contexts.
     *
     * @param config
     */
    public importConfiguration(config : BindingConfiguration) : void
    {
        this._log.debug('Importing binding configuration');

        // Clear existing configuration
        this._bindings.clear();
        this._actions.clear();
        this._contexts.clear();
        this._activeContexts.clear();

        // Import actions
        for(const action of config.actions)
        {
            this.registerAction(action);
        }

        // Import contexts first so they exist before bindings reference them
        for(const contextData of config.contexts)
        {
            this.registerContext(contextData.name, contextData.exclusive);

            // Activate contexts that were active
            if(contextData.active)
            {
                this.activateContext(contextData.name);
            }
        }

        // Import bindings
        for(const bindingDef of config.bindings)
        {
            try
            {
                this.registerBinding(bindingDef);
            }
            catch (err : unknown)
            {
                if(err instanceof Error)
                {
                    this._log.error(`Failed to import binding for action "${ bindingDef.action }": ${ err.message }`);
                }
            }
        }

        this._log.info(`Configuration imported: ${ config.actions.length } actions, `
            + `${ config.bindings.length } bindings, ${ config.contexts.length } contexts`);
    }

    /**
     * Tears down the binding manager and cleans up any resources
     */
    async $teardown() : Promise<void>
    {
        this._log.info('Tearing down BindingManager');

        // Abort any active capture before clearing state
        if(this._captureState)
        {
            const { reject } = this._captureState;
            this._cleanupCapture();
            reject(new Error('BindingManager torn down during captureInput()'));
        }

        // Unsubscribe from input events
        if(this._inputUnsubscribe)
        {
            this._inputUnsubscribe();
            this._inputUnsubscribe = null;
        }

        // Clear all bindings and cached input state
        this._bindings.clear();
        this._lastInputState.clear();

        // Clear all actions
        this._actions.clear();

        // Clear all contexts
        this._contexts.clear();
        this._activeContexts.clear();

        // Reset device tracking
        this._lastActiveDeviceType = null;

        this._log.info('BindingManager torn down successfully');
    }
}

//----------------------------------------------------------------------------------------------------------------------
